import { useState, useEffect } from "react";
import { Link } from "react-router";
import { ArrowRight, Palette, Package, Truck, Target, Users, Zap } from "lucide-react";
import { useInViewOnce } from "../hooks/useInViewOnce";
import { cn } from "../components/ui/utils";
import { HEADLINE_GRADIENT } from "../components/GradientSectionHeading";
import { BrandLogosMarquee } from "../components/BrandLogosMarquee";

export function ServicesPage() {
  const { ref: headlineRef, inView: headlineInView } = useInViewOnce({ threshold: 0.28, rootMargin: "0px 0px -10% 0px" });
  const [showDecor, setShowDecor] = useState(false);

  useEffect(() => {
    const check = () => setShowDecor(window.innerWidth >= 1280);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* HERO SECTION */}
      <section className="relative py-32 bg-[#F6F2EB] overflow-hidden min-h-[768px] flex items-center border-b border-[#131312]/5">
        {/* Connector lines — single full-width SVG stretched across 100% of section */}
        <style>{`
          @keyframes svgRevealL { from { opacity:0; transform:translateX(-50px); } to { opacity:1; transform:translateX(0); } }
          @keyframes svgRevealR { from { opacity:0; transform:translateX(50px); } to { opacity:1; transform:translateX(0); } }
          .svg-lines-left  { animation: svgRevealL 1.2s cubic-bezier(0.16,1,0.3,1) 0.3s both; }
          .svg-lines-right { animation: svgRevealR 1.2s cubic-bezier(0.16,1,0.3,1) 0.3s both; }
          .icon-appear-l { animation: svgRevealL 0.7s cubic-bezier(0.16,1,0.3,1) both; }
          .icon-appear-r { animation: svgRevealR 0.7s cubic-bezier(0.16,1,0.3,1) both; }
        `}</style>
        {showDecor && <svg className="absolute inset-0 w-full h-full pointer-events-none select-none" style={{ maskImage: 'linear-gradient(to right, black 0%, black 22%, transparent 35%, transparent 65%, black 78%, black 100%)', WebkitMaskImage: 'linear-gradient(to right, black 0%, black 22%, transparent 35%, transparent 65%, black 78%, black 100%)' }} viewBox="0 0 1920 720" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="lg-l" x1="588" y1="378" x2="295.5" y2="339.5" gradientUnits="userSpaceOnUse">
              <stop stopColor="#FF8F1B"/><stop offset="0.25" stopColor="#FA2733"/><stop offset="0.5" stopColor="#FD49A8"/><stop offset="0.75" stopColor="#B0ACFE"/><stop offset="1" stopColor="#4F88FF"/>
            </linearGradient>
            <linearGradient id="lg-r" x1="1525" y1="377" x2="1238" y2="340" gradientUnits="userSpaceOnUse">
              <stop stopColor="#4F88FF"/><stop offset="0.25" stopColor="#B0ACFE"/><stop offset="0.5" stopColor="#FD49A8"/><stop offset="0.75" stopColor="#FA2733"/><stop offset="1" stopColor="#FF8F1B"/>
            </linearGradient>
          </defs>
          <g className="svg-lines-left">
            <path d="M681.5 338V340H610.5C591.989 340 582.776 342.781 577.967 350.289C575.527 354.097 574.144 359.229 573.385 366.109C572.626 372.984 572.5 381.495 572.5 392C572.5 398.05 572.723 403.4 571.979 407.89C571.221 412.459 569.448 416.27 565.405 419.243C561.424 422.171 555.336 424.217 546.119 425.545C537.157 426.836 525.126 427.46 508.985 427.496C508.728 432.236 504.804 436 500 436C495.029 436 491 431.971 491 427C491 422.029 495.029 418 500 418C504.458 418 508.156 421.242 508.872 425.496C525.034 425.463 536.989 424.839 545.834 423.564C554.945 422.252 560.638 420.266 564.22 417.632C567.739 415.043 569.31 411.76 570.006 407.563C570.715 403.287 570.5 398.199 570.5 392C570.5 381.506 570.624 372.891 571.396 365.891C572.168 358.896 573.598 353.403 576.283 349.211C581.724 340.719 592.011 338 610.5 338H681.5ZM500 420C496.134 420 493 423.134 493 427C493 430.866 496.134 434 500 434C503.866 434 507 430.866 507 427C507 423.134 503.866 420 500 420ZM307 323C311.632 323 315.444 326.499 315.942 330.998C369.77 330.992 422.744 330.747 477.753 330.5C533.423 330.25 620.175 330 683 330V332C620.18 332 533.431 332.25 477.762 332.5C422.754 332.747 369.775 332.992 315.942 332.998C315.446 337.499 311.633 341 307 341C302.029 341 298 336.971 298 332C298 327.029 302.029 323 307 323ZM307 325C303.134 325 300 328.134 300 332C300 335.866 303.134 339 307 339C310.866 339 314 335.866 314 332C314 328.134 310.866 325 307 325ZM457 224C461.632 224 465.446 227.5 465.943 232H508C519.638 232 528.008 232.685 533.988 234.414C540.02 236.158 543.711 238.994 545.835 243.309C547.915 247.535 548.422 253.057 548.445 259.997C548.457 263.484 548.346 267.377 548.231 271.69C548.117 276.01 548 280.764 548 286C548 295.693 548.012 302.6 549.603 307.645C550.385 310.126 551.541 312.117 553.252 313.743C554.969 315.376 557.298 316.691 560.498 317.737C566.937 319.843 576.692 320.797 591.536 321.211C606.354 321.624 656.112 321.5 682.5 321.5V323.5C656.138 323.5 606.334 323.626 591.479 323.211C576.652 322.797 566.626 321.845 559.877 319.638C556.483 318.528 553.866 317.085 551.875 315.192C549.877 313.293 548.56 310.991 547.694 308.246C545.988 302.837 546 295.557 546 286C546 280.736 546.117 275.959 546.231 271.638C546.346 267.311 546.457 263.453 546.445 260.003C546.422 253.069 545.898 247.965 544.04 244.191C542.226 240.506 539.074 237.967 533.434 236.336C527.742 234.69 519.612 234 508 234H465.943C465.446 238.5 461.632 242 457 242C452.029 242 448 237.971 448 233C448 228.029 452.029 224 457 224ZM457 226C453.134 226 450 229.134 450 233C450 236.866 453.134 240 457 240C460.866 240 464 236.866 464 233C464 229.134 460.866 226 457 226Z" fill="url(#lg-l)" opacity="0.5"/>
          </g>
          <g className="svg-lines-right">
            <path d="M1304 337C1322.49 337 1332.78 339.719 1338.22 348.211C1340.9 352.403 1342.33 357.896 1343.1 364.891C1343.88 371.891 1344 380.506 1344 391C1344 397.199 1343.79 402.287 1344.49 406.563C1345.19 410.76 1346.76 414.043 1350.28 416.632C1353.86 419.266 1359.55 421.252 1368.67 422.564C1377.51 423.839 1389.47 424.463 1405.63 424.496C1406.34 420.242 1410.04 417 1414.5 417C1419.47 417 1423.5 421.029 1423.5 426C1423.5 430.971 1419.47 435 1414.5 435C1409.7 435 1405.77 431.236 1405.51 426.496C1389.37 426.46 1377.34 425.836 1368.38 424.545C1359.16 423.217 1353.08 421.171 1349.09 418.243C1345.05 415.27 1343.28 411.459 1342.52 406.89C1341.78 402.4 1342 397.05 1342 391C1342 380.495 1341.87 371.984 1341.12 365.109C1340.36 358.229 1338.97 353.097 1336.53 349.289C1331.72 341.781 1322.51 339 1304 339H1241.5V337H1304ZM1414.5 419C1410.63 419 1407.5 422.134 1407.5 426C1407.5 429.866 1410.63 433 1414.5 433C1418.37 433 1421.5 429.866 1421.5 426C1421.5 422.134 1418.37 419 1414.5 419ZM1610 322C1614.97 322 1619 326.029 1619 331C1619 335.971 1614.97 340 1610 340C1605.72 340 1602.13 337.007 1601.22 332.998C1547.17 332.993 1493.98 332.747 1438.76 332.5C1382.92 332.25 1307 332 1244 332V330C1307.01 330 1382.93 330.25 1438.77 330.5C1493.92 330.747 1547.03 330.992 1601 330.998C1601 326.028 1605.03 322 1610 322ZM1610 324C1606.13 324 1603 327.134 1603 331C1603 334.866 1606.13 338 1610 338C1613.87 338 1617 334.866 1617 331C1617 327.134 1613.87 324 1610 324ZM1457.5 225C1462.47 225 1466.5 229.029 1466.5 234C1466.5 238.971 1462.47 243 1457.5 243C1452.87 243 1449.05 239.5 1448.56 235H1406.5C1394.89 235 1386.76 235.69 1381.07 237.336C1375.43 238.967 1372.27 241.506 1370.46 245.191C1368.6 248.965 1368.08 254.069 1368.05 261.003C1368.04 264.453 1368.15 268.311 1368.27 272.638C1368.38 276.959 1368.5 281.736 1368.5 287C1368.5 296.557 1368.51 303.837 1366.81 309.246C1365.94 311.991 1364.62 314.293 1362.62 316.192C1360.63 318.085 1358.02 319.528 1354.62 320.638C1347.87 322.845 1337.85 323.797 1323.02 324.211C1308.17 324.626 1268.36 324.5 1242 324.5V322.5C1268.39 322.5 1308.15 322.624 1322.96 322.211C1337.81 321.797 1347.56 320.843 1354 318.737C1357.2 317.691 1359.53 316.376 1361.25 314.743C1362.96 313.117 1364.11 311.126 1364.9 308.645C1366.49 303.6 1366.5 296.693 1366.5 287C1366.5 281.764 1366.38 277.01 1366.27 272.69C1366.15 268.377 1366.04 264.484 1366.05 260.997C1366.08 254.057 1366.59 248.535 1368.67 244.309C1370.79 239.994 1374.48 237.158 1380.51 235.414C1386.49 233.685 1394.86 233 1406.5 233H1448.56C1449.05 228.5 1452.87 225 1457.5 225ZM1457.5 227C1453.63 227 1450.5 230.134 1450.5 234C1450.5 237.866 1453.63 241 1457.5 241C1461.37 241 1464.5 237.866 1464.5 234C1464.5 230.134 1461.37 227 1457.5 227Z" fill="url(#lg-r)" opacity="0.5"/>
          </g>
        </svg>}

        {/* Icon boxes — next to SVG dot endpoints, offset so icon sits beside not on top of dot */}
        {showDecor && <div className="absolute inset-0 pointer-events-none z-10">
          {/* LEFT — icon sits to the outer/left side of each dot */}
          <div className="icon-box size-16 absolute icon-appear-l" style={{ top: 'calc(32.4% - 32px)', left: 'calc(23.8% - 80px)', animationDelay: '0.5s', background: '#FBFAF7', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)' }}>
            <Users className="size-8" />
          </div>
          <div className="icon-box size-16 absolute icon-appear-l" style={{ top: 'calc(46.1% - 32px)', left: 'calc(16% - 80px)', animationDelay: '0.7s', background: '#FBFAF7', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)' }}>
            <Palette className="size-8" />
          </div>
          <div className="icon-box size-16 absolute icon-appear-l" style={{ top: 'calc(59.3% - 32px)', left: 'calc(26% - 80px)', animationDelay: '0.9s', background: '#FBFAF7', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)' }}>
            <Target className="size-8" />
          </div>

          {/* RIGHT — icon sits to the outer/right side of each dot */}
          <div className="icon-box size-16 absolute icon-appear-r" style={{ top: 'calc(32.4% - 32px)', left: 'calc(75.9% + 16px)', animationDelay: '0.5s', background: '#FBFAF7', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)' }}>
            <Zap className="size-8" />
          </div>
          <div className="icon-box size-16 absolute icon-appear-r" style={{ top: 'calc(46% - 32px)', left: 'calc(83.9% + 16px)', animationDelay: '0.7s', background: '#FBFAF7', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)' }}>
            <Truck className="size-8" />
          </div>
          <div className="icon-box size-16 absolute icon-appear-r" style={{ top: 'calc(59.2% - 32px)', left: 'calc(73.7% + 16px)', animationDelay: '0.9s', background: '#FBFAF7', boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)' }}>
            <Package className="size-8" />
          </div>
        </div>}

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-[#FBF7F4] text-[#131312] border border-[#131312]/10 rounded-full text-sm font-medium mb-8 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.15)]">
              End-to-End Solutions
            </div>
            <h1 ref={headlineRef} className="text-[40px] sm:text-[48px] md:text-[56px] lg:text-[60px] xl:text-[70px] 2xl:text-[78.1px] font-bold mb-8 leading-tight text-[#1C1C1C]">
              <span
                className={cn("gradient-text-animate inline", headlineInView && "gradient-text-animate--play")}
                style={{ backgroundImage: HEADLINE_GRADIENT }}
              >
                360° Packaging<br />Services
              </span>
            </h1>
            <p
              className="font-bold leading-[25px] text-[#1C1C1C] mx-auto"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '18.6px',
                maxWidth: '627px',
                letterSpacing: '-0.5px'
              }}
            >
              Complete end-to-end packaging solutions from concept to delivery. Everything you need under one roof.
            </p>
          </div>
        </div>
      </section>

      {/* TRANSITION SPACER */}
      <section className="h-[170px] bg-[#FBFAF7] flex items-center overflow-hidden border-b border-[#131312]/5">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-8">
          <BrandLogosMarquee />
        </div>
      </section>

      <section className="py-20 bg-[#F6F2EB] border-b border-[#131312]/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="group bg-[#FBFAF7] rounded-3xl p-10 hover:shadow-2xl transition-all border border-[#131312]/5">
              <div className="icon-box size-20 mb-6 group-hover:scale-110 transition-transform">
                <Users className="size-10" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Consultation & Strategy</h3>
              <p className="text-muted-foreground mb-6">
                Work with our packaging experts to develop the perfect packaging strategy for your brand.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Brand Analysis</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Material Selection</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Cost Optimization</span>
                </li>
              </ul>
            </div>

            <div className="group bg-[#FBFAF7] rounded-3xl p-10 hover:shadow-2xl transition-all border border-[#131312]/5">
              <div className="icon-box size-20 mb-6 group-hover:scale-110 transition-transform">
                <Palette className="size-10" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Design & Development</h3>
              <p className="text-muted-foreground mb-6">
                Our in-house design team brings your vision to life with creative packaging designs.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Structural Design</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Graphic Design</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>3D Mockups</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Die-Line Creation</span>
                </li>
              </ul>
            </div>

            <div className="group bg-[#FBFAF7] rounded-3xl p-10 hover:shadow-2xl transition-all border border-[#131312]/5">
              <div className="icon-box size-20 mb-6 group-hover:scale-110 transition-transform">
                <Target className="size-10" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Prototyping & Sampling</h3>
              <p className="text-muted-foreground mb-6">
                See and feel your packaging before committing to production with physical samples.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Digital Proofs</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Physical Samples</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Revision Rounds</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Fast Turnaround</span>
                </li>
              </ul>
            </div>

            <div className="group bg-[#FBFAF7] rounded-3xl p-10 hover:shadow-2xl transition-all border border-[#131312]/5">
              <div className="icon-box size-20 mb-6 group-hover:scale-110 transition-transform">
                <Zap className="size-10" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Production & Manufacturing</h3>
              <p className="text-muted-foreground mb-6">
                State-of-the-art production facilities with rigorous quality control.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Offset Printing</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Digital Printing</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Premium Finishing</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Quality Control</span>
                </li>
              </ul>
            </div>

            <div className="group bg-[#FBFAF7] rounded-3xl p-10 hover:shadow-2xl transition-all border border-[#131312]/5">
              <div className="icon-box size-20 mb-6 group-hover:scale-110 transition-transform">
                <Truck className="size-10" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Logistics & Fulfillment</h3>
              <p className="text-muted-foreground mb-6">
                Global shipping and logistics solutions to get your packaging where it needs to be.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>International Shipping</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Inventory Management</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Just-in-Time Delivery</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Order Tracking</span>
                </li>
              </ul>
            </div>

            <div className="group bg-[#FBFAF7] rounded-3xl p-10 hover:shadow-2xl transition-all border border-[#131312]/5">
              <div className="icon-box size-20 mb-6 group-hover:scale-110 transition-transform">
                <Package className="size-10" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Custom Solutions</h3>
              <p className="text-muted-foreground mb-6">
                Specialized packaging solutions tailored to your unique requirements and challenges.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Bespoke Design</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Special Materials</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Technical Support</span>
                </li>
                <li className="flex items-center text-sm text-[#131312]">
                  <div className="size-1.5 bg-[#131312] rounded-full mr-3" />
                  <span>Project Management</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
